import React, {useState} from 'react';

import {useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';
import {generatePath} from 'react-router-dom';
import {toast} from 'react-toastify';

import {ReactComponent as CopyIcon} from 'assets/svg/copy.svg';
import {ProductGroupIcon} from 'components/common/ProductGroupIcon';
import {
    AdditionalInfoContainer,
    HeaderLeft,
    InfoDivider,
    InfoField,
    InfoLabel,
    InfoRow,
    InfoValue,
    ProductCard,
    ProductCode,
    ProductIconWrapper,
    ProductInfo,
    ProductMeta,
    Section,
    SectionTitle,
} from 'components/pages/DocumentsCreate/ConsumerReturn/ReturnInfo/styled';
import {Icon} from 'components/ui-kit/Icon';
import {Link} from 'components/ui-kit/Link';
import {
    ContentColumns,
    CopyButton,
    Id,
    StatusWrapper,
    Title,
    TitleText,
    TitleWrapper,
} from 'components/ui-kit/PageComponents';
import {Spinner} from 'components/ui-kit/Spinner';
import {Stepper} from 'components/ui-kit/Stepper';
import {DOCUMENT_STATUS_COLORS} from 'core/constants/statuses';
import {ROUTE} from 'core/routes';
import {
    Gate as DocumentProductsGate,
    state$ as documentProductsState$,
} from 'models/documents/details/products/table';
import {colors} from 'theme';
import {RemarkingReport} from 'types/documents';
import {useClipboard} from 'utils/custom-hooks';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';
import {toHex} from 'utils/string';

import {Error, ErrorsWrapper} from './styled';

interface RemarkingProps {
    data: RemarkingReport;
}

const GeneralInfo = ({data}: RemarkingProps) => {
    const {t} = useTranslation();
    const {data: products, isLoading} = useStore(documentProductsState$);
    const product = products?.data[0];
    const info = data.reportInfo;
    const reason = info.remarkingReason ?? info.reason;
    const markingCode = product?.markingCode ?? info.markingCode;
    const gtin = product?.gtin ?? info.gtin ?? info.productCode;
    const productLink = markingCode
        ? generatePath(ROUTE.PRODUCTS_DETAIL, {
              id: toHex(`${gtin ?? null}§${markingCode}`),
          })
        : null;

    return (
        <>
            <DocumentProductsGate reportId={data.reportId} />
            <ErrorsWrapper>
                {data.errors?.map((error, index) => (
                    <Error key={index}>{error.message ?? ''}</Error>
                ))}
            </ErrorsWrapper>

            <Section>
                <SectionTitle>{t('pages.documents.details.Remarking.generalInfo')}</SectionTitle>
                <ContentColumns
                    md={3}
                    data={[
                        {
                            reason: handleNil(reason, (value: string) =>
                                t(`dictionaries.documents.remarking.reasons.${value}`),
                            ),
                        },
                        {
                            comment: handleNil(info.comment),
                        },
                    ]}
                    tNameSpace="pages.documents.details.Remarking"
                />
            </Section>

            <Section>
                <SectionTitle>{t('pages.documents.details.Remarking.product')}</SectionTitle>
                <ProductCard>
                    {isLoading ? (
                        <Spinner />
                    ) : (
                        <>
                            <ProductIconWrapper>
                                <ProductGroupIcon
                                    style={{margin: 0, padding: 0}}
                                    value={product?.productGroupCode ?? info.productGroupCode}
                                    width={16}
                                />
                            </ProductIconWrapper>
                            <ProductInfo>
                                {productLink ? (
                                    <Link
                                        to={productLink}
                                        style={{
                                            fontSize: 14,
                                            fontWeight: 600,
                                            lineHeight: '21px',
                                            wordBreak: 'break-all',
                                        }}
                                    >
                                        {markingCode}
                                    </Link>
                                ) : (
                                    <ProductCode>—</ProductCode>
                                )}
                                <ProductMeta>
                                    <span>
                                        {t('pages.documents.details.Remarking.productName')}:{' '}
                                        {product?.productName ?? info.productName ?? '—'}
                                    </span>
                                    <span>
                                        {t('pages.documents.details.Remarking.productCode')}:{' '}
                                        {gtin ?? '—'}
                                    </span>
                                </ProductMeta>
                            </ProductInfo>
                        </>
                    )}
                </ProductCard>
            </Section>
        </>
    );
};

const InfoPair = ({
    left,
    right,
}: {
    left: {label: string; value: string};
    right?: {label: string; value: string};
}) => (
    <InfoRow>
        <InfoField>
            <InfoLabel>{left.label}</InfoLabel>
            <InfoValue>{left.value}</InfoValue>
        </InfoField>
        {right && (
            <InfoField>
                <InfoLabel>{right.label}</InfoLabel>
                <InfoValue>{right.value}</InfoValue>
            </InfoField>
        )}
    </InfoRow>
);

const AdditionalInfo = ({data}: RemarkingProps) => {
    const {t} = useTranslation();
    const info = data.reportInfo;
    const label = (key: string) => t(`pages.documents.details.Remarking.${key}`);

    return (
        <AdditionalInfoContainer>
            <InfoPair
                left={{label: label('documentId'), value: handleNil(data.reportId)}}
                right={{
                    label: label('documentType'),
                    value: t('dictionaries.documents.type.REMARKING'),
                }}
            />
            <InfoPair
                left={{
                    label: label('status'),
                    value: t(`dictionaries.documents.status.${data.status}`),
                }}
                right={{label: label('organization'), value: handleNil(info.organization)}}
            />
            <InfoPair
                left={{
                    label: label('operator'),
                    value: handleNil(info.operator ?? info.createdBy),
                }}
                right={{label: label('source'), value: handleNil(info.source)}}
            />
            <InfoDivider>
                <InfoPair
                    left={{
                        label: label('documentDate'),
                        value: handleNil(data.createdAt, (value: string) =>
                            formatDate(value, 'dd.MM.yyyy, HH:mm'),
                        ),
                    }}
                    right={{
                        label: label('processedAt'),
                        value: handleNil(info.processedAt, (value: string) =>
                            formatDate(value, 'dd.MM.yyyy, HH:mm'),
                        ),
                    }}
                />
            </InfoDivider>
        </AdditionalInfoContainer>
    );
};

export const Remarking = ({data}: RemarkingProps) => {
    const {t} = useTranslation();
    const copy = useClipboard();
    const [step, setStep] = useState(0);

    const copyCode = () => {
        copy(data.reportId);
        toast.success(t('pages.documents.details.idCopied'));
    };

    return (
        <>
            <TitleWrapper>
                <HeaderLeft>
                    <TitleText>
                        <Title>
                            {t('pages.documents.details.Remarking.RemarkingId')}{' '}
                            <Id>{data.reportId}</Id>
                        </Title>
                        <CopyButton
                            icon={
                                <Icon
                                    source={CopyIcon}
                                    width={15}
                                    height={15}
                                    fillColor={colors.brandPrimary}
                                />
                            }
                            onClick={copyCode}
                            hoverIndication
                            label={t('pages.documents.details.copyId')}
                            labelPlace="bottom"
                        />
                    </TitleText>
                    <StatusWrapper color={DOCUMENT_STATUS_COLORS[data.status ?? 'DEFAULT']}>
                        {t(`dictionaries.documents.status.${data.status}`)}
                    </StatusWrapper>
                </HeaderLeft>
            </TitleWrapper>

            <Stepper
                steps={[
                    {
                        label: t('pages.documents.details.Remarking.generalInfo'),
                        component: <GeneralInfo data={data} />,
                    },
                    {
                        label: t('pages.documents.details.Remarking.additionalInfo'),
                        component: <AdditionalInfo data={data} />,
                    },
                ]}
                activeStep={step}
                isClickable
                clickCallback={setStep}
            />
        </>
    );
};
